import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API, eur } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Zap, Flame, FileText, FileSpreadsheet, ChevronLeft, Store } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const MONTHS = ["", "Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];
const monthLabel = (ym) => { try { const [y, m] = ym.split("-"); return `${MONTHS[parseInt(m, 10)]} ${y}`; } catch { return ym; } };
const typeIcon = (t) => t === "gas" ? <Flame className="h-4 w-4 text-orange-500" /> : t === "elektriciteit" ? <Zap className="h-4 w-4 text-amber-500" /> : <Store className="h-4 w-4 text-sky-600" />;

export default function FactuurboekEnergie() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith("/rapporten") ? "/rapporten" : "/factuurboek";
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [loc, setLoc] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/energie/summary", { params: { from_date: from, to_date: to } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const visible = useMemo(() => {
    if (!data) return [];
    return loc ? data.locations.filter((l) => l.location === loc) : data.locations;
  }, [data, loc]);
  const tabs = useMemo(() => {
    const present = (data?.locations || []).map((l) => l.location);
    const base = data?.available_locations || [];
    const extra = present.filter((p) => !base.includes(p));
    return ["", ...base, ...extra];
  }, [data]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ from_date: from, to_date: to, format: fmt });
      if (loc) params.set("location", loc);
      const res = await fetch(`${API}/factuurboek/energie/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Energiekosten_${from}_${to}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Energiekosten geëxporteerd (${fmt.toUpperCase()})`);
    } catch { toast.error("Kon rapport niet exporteren"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="energie-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate(`${base}/overzicht`)} data-testid="energie-back" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1">
            <ChevronLeft className="h-4 w-4" /> Terug naar overzicht
          </button>
          <p className="text-xs font-semibold tracking-widest text-sky-700 uppercase">Energiekosten per vestiging</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Zap className="h-7 w-7 text-sky-600" /> Brustem & De Leesberg — per maand
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy === "pdf"} data-testid="energie-export-pdf" onClick={() => download("pdf")}>
            <FileText className="h-4 w-4 mr-1.5 text-red-600" /> {busy === "pdf" ? "Bezig…" : "Export PDF"}
          </Button>
          <Button variant="outline" disabled={busy === "xlsx"} data-testid="energie-export-xlsx" onClick={() => download("xlsx")}>
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {busy === "xlsx" ? "Bezig…" : "Excel"}
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="energie-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="energie-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <Button className="bg-sky-600 hover:bg-sky-700" data-testid="energie-refresh" onClick={load}>Tonen</Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap border-b pb-3">
        {tabs.map((t) => (
          <Button key={t || "all"} size="sm" variant={loc === t ? "default" : "outline"}
            className={loc === t ? "bg-sky-600 hover:bg-sky-700" : ""}
            data-testid={`energie-tab-${t ? t.replace(/\s/g, "-") : "alle"}`} onClick={() => setLoc(t)}>
            {t || "Alle"}
          </Button>
        ))}
      </div>

      {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> :
        visible.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="energie-empty">
            Nog geen energiekosten. Zet bij het verwerken de categorie op "Nutsvoorzieningen" en kies de vestiging.
          </CardContent></Card>
        ) : (
          <div className="space-y-6">
            {visible.map((L) => (
              <Card key={L.location} className="shadow-sm" data-testid={`energie-card-${L.location.replace(/\s/g, "-")}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2"><Store className="h-5 w-5 text-sky-600" /> {L.location}</CardTitle>
                    <div className="text-sm text-muted-foreground">{L.count} facturen · {eur(L.base)} excl. · {eur(L.vat)} btw</div>
                  </div>
                  <div className="flex gap-2 flex-wrap pt-2">
                    {L.by_type.map((bt) => (
                      <div key={bt.type} className="rounded-md border px-3 py-1.5 flex items-center gap-2 text-sm" data-testid={`energie-type-${L.location.replace(/\s/g, "-")}-${bt.type}`}>
                        {typeIcon(bt.type)} <span className="text-muted-foreground">{bt.label}</span>
                        <span className="font-semibold tabular-nums">{eur(bt.base)}</span>
                      </div>
                    ))}
                  </div>
                  {(L.by_bill_type || []).some((b) => b.type === "voorschot" || b.type === "eindafrekening") && (
                    <div className="flex gap-2 flex-wrap pt-1.5">
                      {(L.by_bill_type || []).filter((b) => b.type === "voorschot" || b.type === "eindafrekening").map((b) => (
                        <div key={b.type} className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 flex items-center gap-2 text-xs" data-testid={`energie-billtype-${L.location.replace(/\s/g, "-")}-${b.type}`}>
                          <span className="text-sky-700 font-medium">{b.type === "voorschot" ? "Voorschot" : "Eindafrekening"}</span>
                          <span className="text-muted-foreground">{b.count}×</span>
                          <span className="font-semibold tabular-nums">{eur(b.base)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Maand</TableHead><TableHead className="text-right">Aantal</TableHead>
                      <TableHead className="text-right">Maatstaf (excl.)</TableHead><TableHead className="text-right">Btw</TableHead><TableHead className="text-right">Totaal</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {L.by_month.map((m) => (
                        <TableRow key={m.month} data-testid={`energie-${L.location.replace(/\s/g, "-")}-${m.month}`}>
                          <TableCell>{monthLabel(m.month)}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{m.count}</TableCell>
                          <TableCell className="text-right tabular-nums">{eur(m.base)}</TableCell>
                          <TableCell className="text-right tabular-nums">{eur(m.vat)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium text-sky-700">{eur(m.base + m.vat)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/40 font-semibold">
                        <TableCell>Totaal {L.location}</TableCell>
                        <TableCell className="text-right">{L.count}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(L.base)}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(L.vat)}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(L.base + L.vat)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
